/**
 * ExploreMovies Page
 * Constellation-based film discovery interface
 */

import { useEffect } from 'react';
import Masonry from 'react-masonry-css';
import { useMovieExploreStore } from '../stores/movie-explore-store';
import { getSavedMovieConstellations } from '../services/movie-explore.service';
import MovieConstellationBuilder from '../components/movie-explore/MovieConstellationBuilder';
import MovieVibeField from '../components/movie-explore/MovieVibeField';
import MovieExploreActions from '../components/movie-explore/MovieExploreActions';
import MovieCard from '../components/MovieCard';
import MovieExploreEmptyState from '../components/movie-explore/MovieExploreEmptyState';

export default function ExploreMovies() {
  const {
    nodes,
    results,
    interpretation,
    loading,
    error,
    coherenceScore,
    cacheHit,
    responseTimeMs,
    setSavedConstellations,
    addNode,
  } = useMovieExploreStore();

  // Load saved constellations on mount
  useEffect(() => {
    loadSavedConstellations();
  }, []);

  const loadSavedConstellations = async () => {
    try {
      const constellations = await getSavedMovieConstellations();
      setSavedConstellations(constellations);
    } catch (err) {
      console.error('Failed to load saved movie constellations:', err);
    }
  };

  const handleAddDirector = (director: string) => {
    addNode({
      type: 'director',
      value: director,
      weight: 0.5,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Cinema</h1>
          <p className="text-sm sm:text-base text-gray-600">
            Map your influences. See where the signal leads.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Sidebar */}
          <div className="lg:col-span-5 space-y-6">
            <MovieConstellationBuilder />
            <MovieVibeField />
            <MovieExploreActions />

            {/* Coherence indicator */}
            {results.length > 0 && coherenceScore > 0 && (
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Constellation Coherence</span>
                  <span className="text-sm font-bold text-gray-900">
                    {(coherenceScore * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${coherenceScore * 100}%`,
                      backgroundColor:
                        coherenceScore > 0.7
                          ? '#16a34a'
                          : coherenceScore > 0.4
                          ? '#d97706'
                          : '#dc2626',
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Right Content - Results */}
          <div className="lg:col-span-7">
            {loading ? (
              // Loading Animation
              <div className="flex flex-col items-center justify-center py-20">
                <div className="relative w-32 h-32 mb-8">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-24 h-24 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"
                      style={{ animationDirection: 'reverse' }}
                    />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h1a1 1 0 010 2h-1v8h1a1 1 0 010 2h-1a2 2 0 01-2-2V6z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Pulling from the shelf...
                </h3>
                <p className="text-gray-600 text-center max-w-md">
                  Tracing {nodes.length} thread{nodes.length !== 1 ? 's' : ''}...
                </p>
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                  <span className="inline-block w-2 h-2 bg-purple-600 rounded-full animate-pulse" />
                  <span>Give us a beat.</span>
                </div>
              </div>
            ) : error ? (
              // Error State
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-red-800 font-bold mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Error
                </h3>
                <p className="text-red-700">{error}</p>
              </div>
            ) : results.length > 0 ? (
              // Results
              <div className="space-y-6">
                {/* Interpretation Box */}
                {interpretation && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-purple-900 mb-1">
                          Where we went with this:
                        </h3>
                        <p className="text-sm text-purple-800">{interpretation}</p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-purple-700 flex-shrink-0">
                        {cacheHit && (
                          <span className="px-2 py-1 bg-purple-100 rounded whitespace-nowrap">
                            Cached
                          </span>
                        )}
                        <span className="px-2 py-1 bg-purple-100 rounded whitespace-nowrap">
                          {(responseTimeMs / 1000).toFixed(1)}s
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Results Header */}
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {results.length} Films
                  </h2>
                </div>

                {/* Masonry Grid */}
                <Masonry
                  breakpointCols={{
                    default: 2,
                    1024: 1,
                  }}
                  className="flex -ml-4 w-auto"
                  columnClassName="pl-4 bg-clip-padding"
                >
                  {results.map((movie) => (
                    <div key={movie.id} className="mb-4">
                      <MovieCard movie={movie} onAddDirector={handleAddDirector} />
                    </div>
                  ))}
                </Masonry>
              </div>
            ) : nodes.length === 0 ? (
              // Empty State - No constellation built
              <MovieExploreEmptyState />
            ) : (
              // Empty State - Constellation built but no results
              <div className="flex flex-col items-center justify-center py-20">
                <span className="text-6xl mb-4">🎞️</span>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Constellation's set.
                </h3>
                <p className="text-gray-600 text-center max-w-md">
                  {nodes.length} node{nodes.length !== 1 ? 's' : ''} locked in. Hit Find Films when you're ready.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
